<template>
  <div>
    <v-col cols="12" md="7" offset-md="8" class="py-0" v-if="responsive == 'PC'">
      <v-row dense>
        <v-col cols="12" sm="6" md="2">
          <v-select dense class="fs-12" v-model="tradeSearchForm.exchange" :items="exchanges" :label="$t('common.exchange')" item-text="name" item-value="code" clearable></v-select>
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-select dense class="fs-12" v-model="tradeSearchForm.side" :items="sides" :label="$t('common.side')" clearable></v-select>
        </v-col>
        <v-col cols="12" sm="5" md="2">
          <v-select dense class="fs-12" v-model="tradeSearchForm.symbol" :items="futuresSymbols" :label="$t('common.symbol')" clearable></v-select>
        </v-col>
        <v-col cols="12" sm="2" md="2">
          <v-btn icon @click="getTradingRecords">
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-col>
    <v-col cols="12" class="px-5" v-if="responsive == 'mobile'">
      <v-row dense>
        <v-col cols="12">
          <div class="d-custom-flex">
             <v-select dense class="fs-12 mr-5" v-model="tradeSearchForm.exchange" :items="exchanges" :label="$t('common.exchange')" item-text="name" item-value="code" clearable></v-select>
            <v-select dense class="fs-12" v-model="tradeSearchForm.side" :items="sides" :label="$t('common.side')" clearable></v-select>
          </div>
          <div class="d-custom-flex">
            <v-select dense class="fs-12 mr-3" v-model="tradeSearchForm.symbol" :items="futuresSymbols" :label="$t('common.symbol')" clearable></v-select>
            <v-btn class="float-right mt-2" x-small icon  @click="getTradingRecords">
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
          </div>
        </v-col>
      </v-row> 
    </v-col>
    <v-col cols="12" md="12" class="pa-0 ma-0 pa-0 px-3" v-if="responsive == 'PC'">
      <v-row dense v-if="tradingRecordLoading"> 
          <v-col cols="12">
              <v-skeleton-loader dense type="table-row-divider@30" class="mx-auto"></v-skeleton-loader>
          </v-col>
      </v-row>
      <v-data-table dense :headers="tradingRecordsHeaders" :items="tradingRecords" hide-default-footer v-if="!tradingRecordLoading" :items-per-page="pageSize" disable-pagination>
        <template v-slot:body="{ items }">
          <tbody>
             <tr v-for="(item,index) in items" :key="index">
              <td :class="CommonJS.formatExchangeColor(item.exchange)">{{ item.exchange }}</td>
              <td>{{ item.symbol }}</td>
              <td class="text-right">{{item.openSide == 'open_long' ? '' : '-'}}{{ item.size }}</td>
              <td class="text-right">{{ formatNumber(item.value) }}</td>
              <td><span v-status="item.status"></span></td>
              <td class="pointer mx-0 px-0" :title="item.openId" @click="copyToClipboard(item.openId)">{{ item.openId.substring(0,10) }}...</td>
              <td :class="item.openSide == 'open_long' ? 'green--text' : 'red--text'"><span v-side="item.openSide"></span></td>
              <td class="px-0 mx-0">{{ formatDate(item.openTimestamp) }}</td>
              <td class="text-right">{{ formatNumber(item.openAvgPrice.toFixed(futuresDecimalNumbers[item.symbol])) }}</td>
              <td class="text-right" :title="item.openCommission">{{ formatNumber(item.openCommission.toFixed(4))+' ' + getFuturesBaseCurrency(item.exchange, item.symbol) }}</td>
              <td class="text-right" :title="item.openRatio">{{ formatNumber((item.openRatio * 100 ).toFixed(4)) }}%</td>
              <td class="pointer mx-0 px-0" :title="item.closeId" @click="copyToClipboard(item.closeId)">{{ item.closeId.substring(0,10) }}...</td>
              <td :class="item.closeSide == 'close_buy' ? 'green--text' : 'red--text'"><span v-side="item.closeSide"></span></td>
              <td class="px-0 mx-0">{{ formatDate(item.closeTimestamp) }}</td>
              <td class="text-right">{{ formatNumber(item.closeAvgPrice.toFixed(futuresDecimalNumbers[item.symbol])) }}</td>
              <td class="text-right" :title="item.closeCommission">{{ formatNumber(item.closeCommission.toFixed(4))+' ' + getFuturesBaseCurrency(item.exchange, item.symbol) }}</td>
              <td class="text-right" :title="item.closeRatio">{{ formatNumber((item.closeRatio * 100 ).toFixed(4)) }}%</td>
              <td class="text-right" :title="item.profit" :class="item.profit > 0 ? 'green--text' : ( item.profit == 0 ? '' : 'red--text')">{{ formatNumber(item.profit.toFixed(8)) + ' ' + getFuturesBaseCurrency(item.exchange, item.symbol)}}</td>
              <td class="text-right" :title="item.profitInUSD" :class="item.profitInUSD > 0 ? 'green--text' : ( item.profitInUSD == 0 ? '' : 'red--text')">{{ item.profitInUSD < 0 ? '($':'$' }}{{ formatNumber(Math.abs(item.profitInUSD.toFixed(2))) }}{{ item.profitInUSD < 0 ? ')':''}}</td>
              <td class="text-right" :title="item.roe" :class="item.roe > 0 ? 'green--text' : ( item.roe == 0 ? '' : 'red--text')">{{ formatNumber((item.roe * 100).toFixed(2)) }}%</td>
            </tr>
            <tr v-if="!tradingRecordLoading && tradingRecordTotalElements == 0">
              <td class="text-center" colspan="20">{{ $t('signalHistory.noDataAvailable')}}</td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
      <v-divider v-if="!tradingRecordLoading"></v-divider>
      <v-row class="pb-5" v-if="tradingRecordTotalElements > 0">
        <v-col cols="auto" class="pl-0">
          <v-pagination color="lightred" circle prev-icon="mdi-menu-left" next-icon="mdi-menu-right" v-model="tradingRecordPageNo"
            :length="tradingRecordTotalPage" :items-per-page="pageSize" :total-visible="15" @input="getTradingRecords" class="ml-2 mt-3">
          </v-pagination>
        </v-col>
        <v-col cols="auto" class="mt-5 pt-5" >
          {{$t("log.totalNumber") + formatNumber(tradingRecordTotalElements)}}
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" sm="6" md="3" class="text-right mt-3">
          <v-btn outlined tile class="ma-2" small @click="exportorCurrent()" :loading="exportorCurrentLoading">
            <v-icon small class="mr-2">mdi-download</v-icon>{{$t("log.downloadCurrent")}}
          </v-btn>
          <!-- <v-btn outlined tile class="ma-2" small @click="exportorAll()" :loading="exportorAllLoading">
            <v-icon small class="mr-2">mdi-download</v-icon>{{$t("log.downloadAll")}}
          </v-btn> -->
        </v-col>
      </v-row>
    </v-col>
    <v-col cols="12" md="12" class="pa-0 ma-0 pa-0 px-3" v-if="responsive == 'mobile'">
      <v-simple-table dense>
        <template v-slot:default>
          <thead>
             <tr>
              <th>{{$t("common.exchange")}}</th>
              <th>{{$t("common.symbol")}}</th>
              <th class="text-right">{{$t("log.size")}}</th>
              <th class="text-right">{{$t("log.value")}}</th>
              <th>{{$t("log.status")}}</th>
              <th>{{$t("log.openId")}}</th>
              <th>{{$t("log.openSide")}}</th>
              <th>{{$t("log.openTime(utc)")}}</th>
              <th class="text-right">{{$t("log.openAvgPrice")}}</th>
              <th class="text-right">{{$t("log.openCommission")}}</th>
              <th class="text-right">{{$t("log.openRatio")}}</th>
              <th>{{$t("log.closeId")}}</th>
              <th>{{$t("log.closeSide")}}</th>
              <th>{{$t("log.closeTime(utc)")}}</th>
              <th class="text-right">{{$t("log.closeAvgPrice")}}</th>
              <th class="text-right">{{$t("log.closeCommission")}}</th>
              <th class="text-right">{{$t("log.closeRatio")}}</th>
              <th class="text-right">{{$t("log.p/l")}}</th>
              <th class="text-right">{{$t("log.roe")}}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="tradingRecordLoading">
              <td colspan="17">
                <v-progress-linear indeterminate ></v-progress-linear>
              </td>
            </tr>
            <tr v-if="!tradingRecordLoading && tradingRecordTotalElements == 0">
              <td class="text-center" colspan="17">{{ $t('log.noDataAvailable')}}</td>
            </tr>
             <tr v-for="(item,index) in tradingRecords" :key="index">
              <td :class="CommonJS.formatExchangeColor(item.exchange)">{{ item.exchange }}</td>
              <td>{{ item.symbol }}</td>
              <td class="pointer mx-0 px-0" :title="item.openId" @click="copyToClipboard(item.openId)">{{ item.openId.substring(0,10) }}...</td>
              <td class="pointer mx-0 px-0" :title="item.closeId" @click="copyToClipboard(item.closeId)">{{ item.closeId.substring(0,10) }}...</td>
              <td class="text-right">{{item.openSide == 'open_long' ? '' : '-'}}{{ item.size }}</td>
              <td class="text-right">{{ formatNumber(item.value) }}</td>
              <td class="px-0 mx-0">{{ formatDate(item.openTimestamp) }}</td>
              <td :class="item.openSide == 'open_long' ? 'green--text' : 'red--text'"><span v-side="item.openSide"></span></td>
              <td class="text-right">{{ formatNumber(item.openAvgPrice.toFixed(futuresDecimalNumbers[item.symbol])) }}</td>
              <td class="text-right">{{ formatNumber(item.openCommission.toFixed(4))+' ' + getFuturesBaseCurrency(item.exchange, item.symbol) }}</td>
              <td class="text-right">{{ formatNumber((item.openRatio * 100 ).toFixed(4)) }}%</td>
              <td><span v-status="item.status"></span></td>
              <td class="px-0 mx-0">{{ formatDate(item.closeTimestamp) }}</td>
              <td :class="item.closeSide == 'close_buy' ? 'green--text' : 'red--text'"><span v-side="item.closeSide"></span></td>
              <td class="text-right">{{ formatNumber(item.closeAvgPrice.toFixed(futuresDecimalNumbers[item.symbol])) }}</td>
              <td class="text-right">{{ formatNumber(item.closeCommission.toFixed(4))+' ' + getFuturesBaseCurrency(item.exchange, item.symbol) }}</td>
              <td class="text-right">{{ formatNumber((item.closeRatio * 100 ).toFixed(4)) }}%</td>
              <td class="text-right" :class="item.profit > 0 ? 'green--text' : ( item.profit == 0 ? '' : 'red--text')">{{ formatNumber(item.profit.toFixed(8)) + ' ' + getFuturesBaseCurrency(item.exchange, item.symbol)}}</td>
              <td class="text-right" :class="item.profitInUSD > 0 ? 'green--text' : ( item.profitInUSD == 0 ? '' : 'red--text')">{{ item.profitInUSD < 0 ? '($':'$' }}{{ formatNumber(Math.abs(item.profitInUSD.toFixed(2))) }}{{ item.profitInUSD < 0 ? ')':''}}</td>
              <td class="text-right" :class="item.roe > 0 ? 'green--text' : ( item.roe == 0 ? '' : 'red--text')">{{ formatNumber((item.roe * 100).toFixed(2)) }}%</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <v-divider></v-divider>
      <v-row class="pb-5" v-if="tradingRecordTotalElements > 0">
        <v-col cols="12" class="pl-0">
          <v-pagination color="lightred" circle prev-icon="mdi-menu-left" next-icon="mdi-menu-right" v-model="tradingRecordPageNo"
            :length="tradingRecordTotalPage" :total-visible="15" @input="getTradingRecords" class="ml-2 mt-3">
          </v-pagination>
        </v-col>
        <v-col cols="auto" class="mt-5 pt-5" >
          {{$t("log.totalNumber") + formatNumber(tradingRecordTotalElements)}}
        </v-col>
        <v-col cols="auto" class="float-right mt-3">
          <v-btn outlined tile class="ma-2" small @click="exportorCurrent()" :loading="exportorCurrentLoading">
            <v-icon small class="mr-2">mdi-download</v-icon>{{$t("log.downloadCurrent")}}
          </v-btn>
          <!-- <v-btn outlined tile class="ma-2" small @click="exportorAll()" :loading="exportorAllLoading">
            <v-icon small class="mr-2">mdi-download</v-icon>{{$t("log.downloadAll")}}
          </v-btn> -->
        </v-col>
      </v-row>
    </v-col>
  </div>
</template>

<script>
  import Vue from 'vue';
  import config from '@/config.js';
  import { mapGetters } from "vuex";
  import CsvExportor from "csv-exportor";

  export default {
    components: {

    },
    data() {
      return {
        tradingRecordsHeaders:[
          { text:this.$t("common.exchange"), sortable:false},
          { text:this.$t("common.symbol"), sortable:false},
          { text:this.$t("log.size"), sortable:false, align:'right'},
          { text:this.$t("log.value"), sortable:false, align:'right'},
          { text:this.$t("log.status"), sortable:false},
          { text:this.$t("log.openId"), sortable:false},
          { text:this.$t("log.openSide"), sortable:false},
          { text:this.$t("log.openTime(utc)"), sortable:false},
          { text:this.$t("log.openAvgPrice"), sortable:false, align:'right'},
          { text:this.$t("log.openCommission"), sortable:false, align:'right'},
          { text:this.$t("log.openRatio"), sortable:false, align:'right'},
          { text:this.$t("log.closeId"), sortable:false},
          { text:this.$t("log.closeSide"), sortable:false},
          { text:this.$t("log.closeTime(utc)"), sortable:false},
          { text:this.$t("log.closeAvgPrice"), sortable:false, align:'right'},
          { text:this.$t("log.closeCommission"), sortable:false, align:'right'},
          { text:this.$t("log.closeRatio"), sortable:false, align:'right'},
          { text:this.$t("log.p/l"), sortable:false, align:'right'},
          { text:this.$t("log.p/lInUsd"), sortable:false, align:'right'},
          { text:this.$t("log.roe"), sortable:false, align:'right'}
        ],
        pageSize:30,
        tradingRecordLoading:false,
        exportorCurrentLoading:false,
        exportorAllLoading:false,
        tradingRecords:[],
        tradingRecordTotalElements:0,
        tradingRecordTotalPage:0,
        tradingRecordPageNo:1,
        tradeSearchForm:{
          exchange:null,
          side:null,
          symbol:null,
        },
        sides: ["Long", "Short"],
      }
    },
    created (){

    },

    destroyed(){

    },
    watch:{
      
    },
    mounted() {
     this.getTradingRecords();
    },
    computed:{
      ...mapGetters(['darkMode','exchanges','futuresDecimalNumbers','exchangeSupportedFuturesSymbols','futuresSymbols','time','responsive']),
    },
    methods: {
      exportorAll(){
        this.exportorAllLoading = true;
      },
      exportorCurrent(){
        this.exportorCurrentLoading = true;
        this.exportorCsvFile(this.tradingRecords);
      },
      exportorCsvFile(tradingRecords){
        var heads = Object.keys(tradingRecords[0]);
        var body =[];
        for( var i = 0 ; i < tradingRecords.length ; i++){
          var line = [];
          heads.map(item=>{
            // 处理时间
            if( item.indexOf("Timestamp") != -1 ){
              if(tradingRecords[i][item] == undefined || tradingRecords[i][item] == null){
                line.push("--");
              }else if( tradingRecords[i][item] == -1 ){
                line.push("-1");
              }else if( tradingRecords[i][item] == 0 ){
                line.push("--");
              }else{
                line.push(this.formatDate(tradingRecords[i][item]));
              }
            }
            if( item.indexOf("Timestamp") == -1 ){
              // 处理无值的
              if(tradingRecords[i][item] == undefined){
                line.push("--");
              }else{
                if(tradingRecords[i][item] == 0){
                  line.push("--");
                }else{
                  // size的处理
                  if(item.indexOf("size") != -1){
                    if(tradingRecords[i]["openSide"] == "open_long" || tradingRecords[i]["openSide"] == "close_buy"){
                      line.push(tradingRecords[i][item]);
                    }else{
                      line.push("-"+tradingRecords[i][item]);
                    }
                  // roe处理
                  }else if(item.indexOf("roe") != -1){
                    line.push(tradingRecords[i][item] * 100 + "%");
                  }else{
                    line.push(tradingRecords[i][item]);
                  }
                }
              }
            }
          })
          body.push(line);
        }
        CsvExportor.downloadCsv(body, { header: heads }, 'Coin-Trading-Record-' +this.user.username + '.csv');
        this.exportorCurrentLoading = false;
        this.exportorAllLoading = false;
      },
      // 获取交易往返记录
      getTradingRecords() {
        this.tradingRecordLoading = true;
        this.tradingRecords = [];
        var last = "";
        if (this.tradeSearchForm.exchange != null) {
          last = last + "&exchange=" + this.tradeSearchForm.exchange;
        }
        if (this.tradeSearchForm.side != null) {
          if (this.tradeSearchForm.side == 'Long') {
            last = last + "&side=open_long"
          }
          if (this.tradeSearchForm.side == 'Short') {
            last = last + "&side=open_short"
          }
        }
        if (this.tradeSearchForm.symbol != null) {
          last = last + "&symbol=" + this.tradeSearchForm.symbol;
        }
        let api = config.baseUrl + "/order/futures/private/getTradingRecords?pageNo=" + this.tradingRecordPageNo +
          "&pageSize=" + this.pageSize + last;
        this.$http.get(api).then(res => {
          let data = res.data;
          if (data.code == 200) {
            this.tradingRecords = data.result.tradingRecords;
            this.tradingRecordTotalElements = data.result.total;
            this.tradingRecordTotalPage = Math.ceil(this.tradingRecordTotalElements / this.pageSize);
            this.tradingRecordLoading = false;
          } else {
            this.tradingRecordTotalElements = 0;
            this.tradingRecordTotalPage = 0;
            this.tradingRecordLoading = false;
          }
        })
      },
      // 格式化日期
      formatDate(timestamp) {
        if ('localTime' == this.time) {
          return formatTime(timestamp);
        } else {
          return formatUTCTime(timestamp);
        }
      },
      // 格式化数字
      formatNumber(num) {
        return formatNumber(num)
      },
      // 获取基础货币
      getFuturesBaseCurrency(exchange, symbol) {
        if (exchange == null || symbol == null) {
          return '';
        }
        let currentExchangeFuturesSymbolInfos = this.exchangeSupportedFuturesSymbols[exchange.toLowerCase()];
        for (let i = 0, len = currentExchangeFuturesSymbolInfos.length; i < len; i++) {
          if (currentExchangeFuturesSymbolInfos[i].symbol == symbol) {
            return currentExchangeFuturesSymbolInfos[i].baseCurrency;
          }
        }
        return '';
      },
      // 复制到剪切板
      copyToClipboard(message) {
        this.$copyText(message).then(res => {
          // 复制成功
          this.$store.dispatch('snackbarMessageHandler', this.$t('common.copied'));
        }).catch(err => {
          // 复制失败
          this.$store.dispatch('snackbarMessageHandler', this.$t('common.copyFailed'));
        })
      },
    }
  }

</script>

<style>

</style>
